import React, { useEffect, useMemo, useState } from "react";
import SectionField from "../section/SectionField";
import styles from "./MultipleChoice.module.scss";
import InputTextArea from "components/shared/textArea/InputTextArea";
import FileUploadButton from "components/shared/fileUploadButton/FileUploadButton";
import { Controller, useForm } from "react-hook-form";
import TriggerButton from "components/shared/createTriggerButton/TriggerButton";
import { useDispatch, useSelector } from "react-redux";
import { setDroppedItems, setUpdateDroppedItems } from "redux/slices/formSlice";
import { capitalizeFirstLetter, handleFile, randomIntFromInterval } from "utils/utils";
import { useDrag, useDrop } from "react-dnd";
import DeleteIcon from "@mui/icons-material/Delete";
import { setPublish } from "redux/slices/globalSlice";
import { setDroppedItems as setTriggerList } from "redux/slices/triggerSlice";
import { useNavigate, useParams } from "react-router-dom";
import { Select } from "@mui/material";
import MenuItem from '@mui/material/MenuItem';
import toast from "react-hot-toast";
import { fromJS } from "immutable";
const ITEM_TYPE = "OPTION";
const DraggableOption = ({ option, index, moveOption, handleChangeOption }) => {
  const [{ isDragging }, dragRef] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });


  const [, dropRef] = useDrop({
    accept: ITEM_TYPE,
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveOption(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => dragRef(dropRef(node))}
      className={styles.answerRow}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className={styles?.draggableHandle}></div>
      <input
        type="text"
        value={option}
        onChange={(e) => handleChangeOption(index, e.target.value)}
      />
      <button
        type="button"
        className={styles.buttons}
        onClick={() => handleChangeOption(index, "")}
      >
        Remove
      </button>
    </div>
  );
};

const MultipleChoice = () => {
  const { publish } = useSelector((state) => state?.global);
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const { formId } = useParams();
  const { triggerDroppedList } = useSelector((state) => state.trigger);
  const { droppedItems, selectedData } = useSelector((state) => state.form);
  const [file, setFile] = useState("");
  const [newOption, setNewOption] = useState("");

  const { register, control, watch, setValue: setValues, formState: { isDirty } } = useForm({
    defaultValues: {
      showInstructions: false,
      allowMultipleAnswers: selectedData?.allowMultipleAnswers || false,
      options: selectedData?.options || ["Yes", "No", "Maybe"],
      whenAnswerEquals: "",
      instructions: selectedData?.instructions || "",
      enableDependentQuestions: selectedData?.whenAnswerEquals ? true : false,
    },
  });


  const handleFileChange = (file, setIsLoading) => {
    setFile(file);
    handleFile(file, droppedItems, selectedData, dispatch, setDroppedItems, setIsLoading);;
    dispatch(setPublish(true));
  };

  const handleChangeOption = (index, newValue) => {
    const updatedOptions = [...watch("options")];
    if (newValue === "") {
      updatedOptions.splice(index, 1);
    } else {
      updatedOptions[index] = newValue;
    }
    setValues("options", updatedOptions);
  };

  const handleAddOption = () => {
    if (newOption.trim() !== "") {
      const updatedOptions = [...watch("options"), capitalizeFirstLetter(newOption)];
      setValues("options", updatedOptions);
      setNewOption("");
    }
  };

  const moveOption = (fromIndex, toIndex) => {
    const updatedOptions = [...watch("options")];
    const [movedOption] = updatedOptions.splice(fromIndex, 1);
    updatedOptions.splice(toIndex, 0, movedOption);
    setValues("options", updatedOptions);
  };


  const handleImageDelete = (src, selectedItem) => {
    const updatedMedia = selectedItem?.data?.media.filter(
      (media) => media.path !== src
    );

    const updatedData = { media: updatedMedia };

    dispatch(setUpdateDroppedItems({ id: selectedData.id, updatedData }));
    setFile("");
  };

  const handleOpenImage = (src) => {
    window.open(src, "__blank");
  };

  const currentItem = useMemo(() => {
    let foundItem = droppedItems.find((item) => item.id === selectedData.id);

    if (!foundItem) {
      droppedItems.forEach((item) => {
        const dependentMatch = item.dependentField.find(
          (depField) => depField.id === selectedData.id
        );
        if (dependentMatch) {
          foundItem = dependentMatch;
        }
      });
    }

    return foundItem;
  }, [droppedItems, selectedData]);

  const handleTrigger = () => {
    const uniqueId = Date.now() + randomIntFromInterval(1000, 9999);
    dispatch(setTriggerList([
      ...triggerDroppedList, {
        ...currentItem,
        id: uniqueId
      }
    ]))
    navigate(`/create/triggers/${formId || ""}`)
  }

  useEffect(() => {
    if (watch("enableDependentQuestions")) {
      if (selectedData?.whenAnswerEquals) {
        setValues("whenAnswerEquals", capitalizeFirstLetter(selectedData?.whenAnswerEquals))
      } else {
        setValues("whenAnswerEquals", watch("options")[0])
      }
    } else {
      setValues("whenAnswerEquals", "")
      let clearDrop = droppedItems?.map((e) => {
        return e?.id == selectedData?.id ? { ...e, dependentField: [] } : e
      })
      dispatch(setDroppedItems(clearDrop))
    }
  }, [watch("enableDependentQuestions"), newOption])


  useEffect(() => {
    if (!isDirty) {
      const data = structuredClone(watch())
      dispatch(setUpdateDroppedItems({ id: selectedData.id, updatedData: data }));
    }

    const subscription = watch((data) => {
      const formData = structuredClone(data)
      dispatch(setUpdateDroppedItems({ id: selectedData.id, updatedData: formData }));
      dispatch(setPublish(true));
    });
    return () => subscription.unsubscribe();
  }, [watch]);


  return (
    <>
      <SectionField
        placeholder="Please enter your question here."
        message="Required"
      />

      <div className={styles?.instructionsForm}>
        <h4>Instructions</h4>
        <div className={styles?.formField}>
          <Controller
            name="instructions"
            control={control}
            render={({ field }) => (
              <InputTextArea
                {...field}
                control={control}
                type="textarea"
                name="Optional"
                placeholder="Optional"
                onChange={(e) => {
                  field.onChange(e);
                }}
                className={styles.textArea}
              />
            )}
          />
        </div>
        <div className={styles?.formField}>
          {currentItem?.data?.media?.length ? (
            <div className={styles.imageContainer}>
              {currentItem?.data?.media?.map((src, index) => (
                <div
                  className={styles.imageWrapper}
                  onClick={() => handleOpenImage(src.path)}
                >
                  <img src={src.path} alt="" />
                  <div
                    className={styles.cancelIcon}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleImageDelete(src.path, currentItem);
                    }}
                  >
                    <DeleteIcon sx={{ width: "20px", height: "20px" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <FileUploadButton
              name="mediaFile"
              control={control}
              onFileChange={handleFileChange}
            />
          )}
        </div>
      </div>

      <form className={styles.formContainer}>
        <div>
          <label>
            <input
              type="checkbox"
              disabled={
                !watch("instructions") && !file && !currentItem?.data?.media?.length > 0
              }
              {...register("showInstructions")}
            />
            Show instructions by default
          </label>
        </div>

        <div className={styles.answersContainer}>
          <div className={styles.answersContainerHead}>
            <h4>Options</h4>
            <div>
              <label className={styles.AllowContainer}>
                <Controller
                  name="allowMultipleAnswers"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value}
                      {...register("allowMultipleAnswers")}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        dispatch(setPublish(true))
                      }}
                    />
                  )}
                />

                Allow Multiple Answers
              </label>
            </div>
          </div>

          {watch("options").map((option, index) => (
            <DraggableOption
              key={index}
              option={option}
              index={index}
              moveOption={moveOption}
              handleChangeOption={handleChangeOption}
            />
          ))}

          <div className={styles.addOptionContainer}>
            <input
              className={styles.addOptionInput}
              type="text"
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              placeholder="Add a new option"
            />
            <button
              className={styles.addOptionButton}
              type="button"
              onClick={handleAddOption}
            >
              Add
            </button>
          </div>
        </div>

        <div>
          <label>
            <Controller
              name={`enableDependentQuestions`}
              control={control}
              render={({ field }) => (
                <input
                  type="checkbox"
                  {...field}
                  checked={field.value}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      if (window.confirm("Un-checking this option will remove all dependent questions and any data collected from those questions. Are you sure?")) {
                        field.onChange(e)
                      } else {
                        return
                      }
                    }
                    field.onChange(e)
                  }}

                />


              )}
            />
            Enable Dependent Questions

          </label>
          {
            watch("enableDependentQuestions") && <>
              <label style={{ margin: "10px 0px" }}>Add Dependent Question When Answer Is:</label>
              <div>
                <Controller

                  name="whenAnswerEquals"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} sx={{ width: "100%", height: "40px" }}
                      onChange={(e) => {
                        field.onChange(e);
                      }}>
                      {
                        ["Any", ...watch("options")]?.map((option) => {
                          return <MenuItem value={option}>{option}</MenuItem>
                        })

                      }
                    </Select>
                  )}

                />
              </div>
            </>
          }
        </div>
        <TriggerButton handleClick={() => {
          if (!publish) {
            handleTrigger()
          } else {
            toast.error("There are unpublished changes");
          }
        }

        } />

      </form>
    </>
  );
};

export default MultipleChoice;
